import * as React from 'react';
import { Select } from 'antd/es';
import { SelectProps } from 'antd/es/select';
import { useReopenOnOrientation, useMediaQuery } from '../Helper/OrientationHelper';

// A phone held sideways leaves no room under the list for the keyboard, so the options are not searchable
// there. The short side is what tells a phone from a tablet in that orientation: it is the height here,
// and 600px is below every tablet and above every phone in landscape
const NO_SEARCH_QUERY = '(max-width: 991px) and (orientation: landscape) and (max-height: 599px)';

interface OptionsThumbState {
  top: number;
  height: number;
}

// The mobile keyboard needs ~300ms to slide away, so the search input is blurred back down once that is
// over - the dropdown's own close animation is shorter than this, so it is never cut.
const KEYBOARD_DISPLAY_DELAY = 350;

// antd only renders its own (always visible) scrollbar together with the virtual list, and iOS hides overlay
// scrollbars, so the position inside the list is drawn on top of the container instead.
function SelectOptionsScroll({ children }: { children?: React.ReactNode }) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = React.useState<OptionsThumbState | null>(null);

  const updateThumb = React.useCallback(() => {
    const el = scrollRef.current;
    let next: OptionsThumbState | null = null;

    if (el && el.scrollHeight > el.clientHeight) {
      const height = Math.max(24, (el.clientHeight / el.scrollHeight) * el.clientHeight);
      const top = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * (el.clientHeight - height);
      next = { top, height };
    }

    // Keep the previous object when nothing moved, otherwise setting state here would re-render the
    // component on every layout effect
    setThumb((previous: OptionsThumbState | null) => (
      previous && next && previous.top === next.top && previous.height === next.height
        ? previous
        : next
    ));
  }, []);

  // The options change while searching, so the thumb is recomputed after each render
  React.useLayoutEffect(updateThumb);

  return <div className={'select-options-wrapper'}>
    <div className={'select-options-scroll'} ref={scrollRef} onScroll={updateThumb}>
      {children}
    </div>
    {thumb &&
      <div className={'select-options-thumb'} style={{ top: thumb.top, height: thumb.height }} />
    }
  </div>;
}

/**
 * The `Select` this app uses for a list of options: searchable, and scrolled by a native container instead of
 * rc-virtual-list (a touch drag on that one scrolls the page rather than the list - ant-design#37674). Shared
 * by the tree type picker and the map's Trees filters, so both scroll and search the same way.
 *
 * Without an explicit `optionFilterProp`, a plain `options` list is searched on its `label` and `Option`
 * children on their text.
 */
export default function SearchableSelect(props: SelectProps<any>) {
  const { children, options, filterOption, optionFilterProp, onChange, open: openProp, onDropdownVisibleChange, showSearch, ...rest } = props;
  const [open, setOpen] = React.useState(false);
  const noSearch = useMediaQuery(NO_SEARCH_QUERY);
  // A list opened in the other orientation keeps the width it measured there until it is rendered again
  useReopenOnOrientation(open, setOpen);

  // With `showSearch` the Select keeps its search input focused, which leaves the mobile keyboard open
  const closeKeyboardAfterDropdownCloses = () => {
    window.setTimeout(() => {
      if (document.activeElement instanceof HTMLInputElement) {
        document.activeElement.blur();
      }
    }, KEYBOARD_DISPLAY_DELAY);
  };

  return <div>
    <Select
      {...rest}
      // Height of the option list: 5 options of 32px (`@select-dropdown-height`). Keep in sync with
      // `max-height` of `.select-options-scroll` in main.less.
      listHeight={160}
      // Render the options natively and let `.select-options-scroll` handle the scrolling
      virtual={false}
      showSearch={showSearch ?? !noSearch}
      optionFilterProp={optionFilterProp ?? (options ? 'label' : 'children')}
      filterOption={filterOption}
      options={options}
      open={openProp ?? open}
      onDropdownVisibleChange={(next: boolean) => {
        setOpen(next);
        onDropdownVisibleChange?.(next);
      }}
      // The wrapper is what keeps the option list in the page flow instead of on `body`: it is the
      // `parentElement` the dropdown is rendered into
      getPopupContainer={(triggerNode) => triggerNode.parentElement}
      dropdownRender={(menu: React.ReactElement) => <SelectOptionsScroll>{menu}</SelectOptionsScroll>}
      onChange={(value: any, option: any) => {
        onChange?.(value, option);
        closeKeyboardAfterDropdownCloses();
      }}
    >
      {children}
    </Select>
  </div>;
}
